<script setup lang="ts">
import type { InputPrimaryEvent } from '@shared/ui/fields'
import type { InteractionEditorProps } from '../../../../model/assessments/interaction-editors.hook'

import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { InputPrimary } from '@shared/ui/fields'
import { BtnIcon, BtnSecondary } from '@shared/ui/buttons'

import AnnotatedTextarea from '../AnnotatedTextarea.vue'
import { useInteractionDragTheWords } from '../../../../model/assessments/interaction-editors.hook'
import { MARKER_INTERACTION_COPY } from '../../../../model/assessments/interaction-labels'

defineOptions({
  name: 'InteractionDragTheWords',
})

const props = defineProps<InteractionEditorProps>()

const copy = MARKER_INTERACTION_COPY.drag_the_words

const {
  refText,
  refMarkedWords,
  refDistractors,
  setText,
  setDistractor,
  addDistractor,
  removeDistractor,
} = useInteractionDragTheWords({
  screenId: () => props.screenId,
  activityId: () => props.activityId,
  interaction: () => props.interaction,
})
</script>

<template>
  <div class="interaction-drag-the-words">
    <!-- Copy comes from the shared `MARKER_INTERACTION_COPY`, deliberately parallel to
         `fill_in_blanks`: both interactions are authored by wrapping each answer in a marker, and
         the two hints differ only in what the kid does with the gap. The markers ARE the answer
         key in both, so there is nothing for a count to disagree with — the counter alone is the
         useful signal. -->
    <AnnotatedTextarea
      :label="copy.label"
      :value="refText"
      :markers="['drag']"
      :placeholder="copy.placeholder"
      @update:value="setText"
    />

    <!-- The words the counter just counted, spelled out in gap order. Same `DRAG_GAP_REGEX` behind
         both, so the list and the "N draggable words" line above it cannot disagree. A marker whose
         content is only whitespace is a real gap with a blank answer — shown as `(blank)` rather
         than as an empty chip, the same wording the preview uses. -->
    <ul
      v-if="refMarkedWords.length"
      class="interaction-drag-the-words__words"
    >
      <li
        v-for="word in refMarkedWords"
        :key="word.key"
        class="interaction-drag-the-words__word"
        :title="`Gap ${word.position}`"
      >
        {{ word.value || '(blank)' }}
      </li>
    </ul>

    <AppText name="body2-medium">
      Distractors
    </AppText>
    <div
      v-for="distractor in refDistractors"
      :key="distractor.index"
      class="interaction-drag-the-words__distractor"
    >
      <InputPrimary
        name="distractor"
        :label="`Distractor ${distractor.position}`"
        :value="distractor.value"
        placeholder="A word that fits nowhere"
        @input="(event: InputPrimaryEvent) => setDistractor(distractor.index, String(event.value))"
      />
      <BtnIcon
        :icon="faTrashCan"
        size="small"
        color="red"
        title="Remove distractor"
        @click="removeDistractor(distractor.index)"
      />
    </div>

    <BtnSecondary
      text="Add distractor"
      @click="addDistractor"
    />
  </div>
</template>

<style scoped lang="scss" src="./InteractionDragTheWords.scss" />
